import classNames from 'classnames';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import { FC, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CantoiaLogo from '../../../assets/img/logos/cantoia.png';
import MESSAGES from '../../../common/data/constants/messages';
import { ILogin } from '../../../common/data/dtos/auth';
import Alert from '../../../components/bootstrap/Alert';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody, CardFooter } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Spinner from '../../../components/bootstrap/Spinner';
import { showWarning } from '../../../components/extras/Notifications';
import AuthContext from '../../../contexts/authContext';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import authService, { ILoginResponse } from '../../../services/auth-service';

interface ILoginHeaderProps {
    isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
    if (isNewUser) {
        return (
            <>
                <div className="text-center h1 fw-bold mt-5">Criar Conta,</div>
                <div className="text-center h4 text-muted mb-5">Cadastre-se para começar!</div>
            </>
        );
    }
    return (
        <>
            <div className="text-center h1 fw-bold mt-5">Bem-vindo</div>
            <div className="text-center h4 text-muted mb-5">Faça login para continuar!</div>
        </>
    );
};
LoginHeader.defaultProps = {
    isNewUser: false,
};

interface ILoginProps {
    isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {

    const [signInPassword, setSignInPassword] = useState<boolean>(false);
    const [signUpStatus, setSignUpStatus] = useState<boolean>(!!isSignUp);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [userName, setUserName] = useState<string>('');
    const { setUser } = useContext(AuthContext);

    const navigate = useNavigate();
    const handleOnClick = useCallback(() => navigate('/'), [navigate]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            loginUsername: '',
            loginPassword: '',
        },
        validate: (values) => {
            const errors: { loginUsername?: string; loginPassword?: string } = {};

            if (!values.loginUsername) {
                errors.loginUsername = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }
            else {
                const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                const isValid = emailRegex.test(values.loginUsername);

                if (!isValid)
                    errors.loginUsername = MESSAGES.VALIDATION.INVALID_FORMAT;
            }

            if (!values.loginPassword) {
                errors.loginPassword = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            return errors;
        },
        validateOnChange: false,
        onSubmit: (values) => {
            handleAuthenticate(values);
        },
    });

    const handleContinue = async () => {
        await handleCheckFirstAccess();
    };

    const handleCheckFirstAccess = async () => {

        setIsLoading(true);

        const response = await authService.checkFirstAccess(formik.values.loginUsername.toLowerCase());

        setIsLoading(false);

        if (response.success) {
            if (response.entity?.firstAccess) {
                if (setUser) {
                    setUser(formik.values.loginUsername);
                }
                navigate('/auth-pages/first-access');
            } else {
                setUserName(response.entity?.name ?? '')
                setSignInPassword(true);
            }
        } else {
            setSignInPassword(false);
            showWarning(response.message || MESSAGES.LOGIN.EMPLOYEE_NOT_FOUND);
        }
    };

    const handleAuthenticate = async (values: any) => {

        const login: ILogin = {
            username: values.loginUsername,
            password: values.loginPassword,
        };

        setIsLoading(true);

        const response = await authService.doLogin(login);

        setIsLoading(false);

        if (response.success) {

            setCookies(response);

            setSuccessMessage(MESSAGES.LOGIN.CODE_SENDED);
            setShowSuccessMessage(true);

            setTimeout(() => {
                navigate('/auth-pages/token');
            }, 3000);
        } else {
            showWarning(response.message ?? MESSAGES.LOGIN.ERROR);
        }
    };

    const setCookies = (response: IBaseObject<ILoginResponse>) => {

        if (response.entity && response.entity.companyId) {
            Cookies.set('companyId', response.entity.companyId);
        }
        if (response.entity && response.entity.employeeId) {
            Cookies.set('employeeId', response.entity.employeeId);
        }
        if (response.entity && response.entity.session) {
            Cookies.set('session', response.entity.session);
        }

        // Store user email in cookie for TokenPage
        Cookies.set('userEmail', formik.values.loginUsername);
    }

    return (
        <PageWrapper isProtected={false} title={signUpStatus ? 'Cadastro' : 'Entrar'} className={classNames({ 'bg-dark': !signUpStatus, 'bg-light': signUpStatus })}>
            <Page className="p-0">
                <div className="row h-100 align-items-center justify-content-center">
                    <div className="col-xl-4 col-lg-6 col-md-8 shadow-3d-container">
                        <Card className="shadow-3d-dark" data-tour="login-page">
                            <CardBody>
                                <div className="text-center my-1">
                                    <img src={CantoiaLogo} alt='Cantoia' width={300} />
                                </div>

                                <LoginHeader isNewUser={signUpStatus} />

                                {showSuccessMessage && (
                                    <Alert isLight icon="Email" color="success" className="mb-4">
                                        {successMessage}
                                    </Alert>
                                )}

                                <form className="row g-4">
                                    <>
                                        <div className="col-12">
                                            <FormGroup
                                                id="loginUsername"
                                                isFloating
                                                label="Entre com seu e-mail"
                                                className={classNames({
                                                    'd-none': signInPassword,
                                                })}>
                                                <Input
                                                    autoComplete="username"
                                                    value={formik.values.loginUsername}
                                                    isTouched={formik.touched.loginUsername}
                                                    invalidFeedback={formik.errors.loginUsername}
                                                    isValid={formik.isValid}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        formik.setErrors({});
                                                    }}
                                                />
                                            </FormGroup>

                                            {
                                                signInPassword && (
                                                    <div className="text-center h4 mb-3 fw-bold">
                                                        Olá, {userName}
                                                    </div>
                                                )
                                            }

                                            <FormGroup
                                                id="loginPassword"
                                                isFloating
                                                label="Entre com sua senha"
                                                className={classNames({
                                                    'd-none': !signInPassword,
                                                })}>
                                                <Input
                                                    type="password"
                                                    autoComplete="current-password"
                                                    value={formik.values.loginPassword}
                                                    isTouched={formik.touched.loginPassword}
                                                    invalidFeedback={formik.errors.loginPassword}
                                                    isValid={formik.isValid}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                            </FormGroup>
                                        </div>

                                    </>
                                </form>
                            </CardBody>
                            <CardFooter>
                                {!signInPassword ? (
                                    <Button color="info" className="w-100 py-3" isDisable={!formik.values.loginUsername} onClick={handleContinue}>
                                        {isLoading && <Spinner isSmall inButton isGrow />}
                                        Continuar
                                    </Button>
                                ) : (
                                    <Button color="info" className="w-100 py-3" isDisable={!formik.values.loginPassword} onClick={formik.handleSubmit}>
                                        Entrar
                                    </Button>
                                )}
                            </CardFooter>
                        </Card>
                    </div>
                </div>
            </Page>
        </PageWrapper>
    );
};
Login.propTypes = {
    isSignUp: PropTypes.bool,
};
Login.defaultProps = {
    isSignUp: false,
};

export default Login;
